import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Button } from 'antd';
import PropTypes from 'prop-types';
import {
  AdjustQuantity, ConsTableExclude, ConsTableReview, consHistReviewcols,
} from './ConsHistoryFunctions';
import { GRAPH_TYPES } from '../tabGraph/TabGraphHelpers';
import { getTabOptions } from '../../TabOptions';
import { generateTabHeaders } from '../../../components/helpers';
import TabGraph from '../tabGraph/TabGraph';
import TableTab from '../tableTab/TableTab';
import allActions from '../../../../../actions';

export default function ConsumptionHistoryContainer({
  loadingKey,
  activeTabKey,
  data,
  excludePM10,
  clientHeight,
}) {
  const columnMapper = useSelector((state) => state.commonState.columnMapper);

  const [filteredConsumption, updateFilteredConsumption] = useState([]);
  const [headers, setHeaders] = useState(null);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [updatedData, setUpdatedData] = useState([]);
  const [enableSave, setEnableSave] = useState(false);
  const { plant, material } = useParams();

  const tabOptions = getTabOptions(plant, material, excludePM10).consumptionHistoryOptions;

  const {
    matID, itemID, docYr, outlrOld, outlrNew, qtyOld, qtyNew,
  } = consHistReviewcols;

  const filteredConsumptionData = useMemo(() => (tabOptions?.filter
    ? filteredConsumption?.filter(
      (val) => tabOptions.filter(val),
    )
    : filteredConsumption), [filteredConsumption]);

  const addNewCols = (oldRec, newRec, options, updatedCol) => {
    const newCols = {};
    if (updatedCol === qtyNew) {
      if (!oldRec) {
        newCols[outlrOld] = options.rowClassName(newRec) === 'excluded';
        newCols[outlrNew] = newCols[outlrOld];
      } else {
        newCols[outlrNew] = oldRec[outlrNew];
        newCols[outlrOld] = oldRec[outlrOld];
      }
    } else if (!oldRec) newCols[qtyNew] = newRec[qtyOld];
    else newCols[qtyNew] = oldRec[qtyNew];

    return { ...newRec, ...newCols };
  };

  const checkReverted = (x) => {
    console.log(x);
    return true;
  };

  const updateTableData = useCallback((rec, updatedCol) => {
    let recExists = false;
    let tempData = updatedData?.map((x) => {
      if (x[matID] === rec[matID] && x[itemID] === rec[itemID] && x[docYr] === rec[docYr]) {
        recExists = true;
        return { ...addNewCols(x, rec, tabOptions, updatedCol) };
      }
      return { ...x };
    });
    if (!tempData) tempData = [];
    if (!recExists) tempData.push({ ...addNewCols(null, rec, tabOptions, updatedCol) });
    setUpdatedData(tempData.filter(checkReverted));
  }, [updatedData]);

  const getEditableCols = () => ([
    {
      key: qtyNew,
      title: 'Adj Qty',
      dataIndex: qtyNew,
      width: 80,
      render: (_v, rec) => (
        <AdjustQuantity
          record={rec}
          value={rec?.ADJUSTED_QUANTITY_FROM_UI}
          updateTableData={updateTableData}
          enableSave={enableSave}
          setEnableSave={setEnableSave}
        />
      ),
    }, {
      key: outlrNew,
      title: 'Excl',
      dataIndex: outlrNew,
      width: 45,
      render: (_v, rec) => (
        <ConsTableExclude
          record={rec}
          updateTableData={updateTableData}
          isExcluded={tabOptions.rowClassName(rec) === 'excluded'}
        />
      ),
    },
  ]);

  useEffect(() => {
    if (data?.length >= 1 && columnMapper?.length >= 1) {
      const keys = Object.keys(data[0]);
      const tempHeaders = generateTabHeaders(keys, columnMapper, tabOptions, data, false);
      tempHeaders.push(...getEditableCols());
      setHeaders(tempHeaders);
    }
  }, [data, columnMapper, updatedData]);

  useEffect(() => {
    setUpdatedData([]);
  }, [data]);

  return (
    <>
      <div id="tabRealConsGraph" style={{ height: '40%' }}>
        <TabGraph
          type={GRAPH_TYPES.consumption}
          filteredData={filteredConsumptionData}
          postingDate="EDAM_POSTING_DT"
          containerID="tabRealConsGraph"
          activeTabKey={activeTabKey}
        />
      </div>
      <div style={{ overflowX: 'hidden' }}>
        <TableTab
          loadingKey={loadingKey}
          selector={(state) => state.materialDetailsState.consumptionHistory}
          action={allActions.MaterialDetailsActions.fetchConsumptionHistory}
          title="Material Consumption Data"
          tabOptions={tabOptions}
          updateFilterData={updateFilteredConsumption}
          containerID="tabRealConsTable"
          data={data}
          headers={headers}
          lowerHeight
          clientHeight={clientHeight}
          footer={(
            <div style={{ height: '5%' }}>
              {headers && data ? (
                <Button
                  type="primary"
                  style={{ marginTop: '5px', float: 'right' }}
                  onClick={() => setReviewVisible(true)}
                  disabled={!updatedData?.length && !enableSave}
                >
                  Save Changes
                </Button>
              ) : null}
            </div>
          )}
        />
      </div>
      <ConsTableReview
        visible={reviewVisible}
        setVisible={setReviewVisible}
        data={updatedData}
      />
    </>
  );
}

ConsumptionHistoryContainer.propTypes = {
  loadingKey: PropTypes.string,
  activeTabKey: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.any),
  excludePM10: PropTypes.bool.isRequired,
  clientHeight: null,
};

ConsumptionHistoryContainer.defaultProps = {
  loadingKey: false,
  data: [],
  clientHeight: PropTypes.number,
};
